import { apiClient } from './client'

export interface SecurityStatus {
  isFirstLogin: boolean
  hasPIN: boolean
  pinAttempts: number
  isPINLocked: boolean
  pinLockedUntil?: string
  remainingLockoutMinutes: number
  emailVerified: boolean
  hasSecurityQuestions: boolean
  lastPasswordChange: string
  accountCreated: string
}

export interface SetupPINRequest {
  pin: string
  newPassword: string
}

export interface VerifyPINRequest {
  pin: string
}

export interface ChangePINRequest {
  currentPin: string
  newPin: string
}

export interface InitiatePINResetRequest {
  email: string
}

export interface CompletePINResetRequest {
  resetToken: string
  otpCode: string
  newPin: string
  newPassword?: string
}

export interface SendEmailOTPRequest {
  // No additional fields needed - uses authenticated user
}

export interface VerifyEmailOTPRequest {
  otpCode: string
}

// ==================== FIRST-TIME SETUP ====================

// Set up PIN and password for first-time users
export const setupPIN = async (data: SetupPINRequest): Promise<{
  success: boolean
  message: string
  data: { message: string }
}> => {
  const response = await apiClient.post('/pin/protected/setup-pin', data)
  return response.data
}

// Get current user's security status
export const getSecurityStatus = async (): Promise<{
  success: boolean
  data: SecurityStatus
  message: string
}> => {
  const response = await apiClient.get('/pin/protected/security-status')
  return response.data
}

// ==================== PIN MANAGEMENT ====================

// Verify PIN for sensitive operations
export const verifyPIN = async (data: VerifyPINRequest): Promise<{
  success: boolean
  message: string
  data?: { message: string }
  attemptsRemaining?: number
  lockedUntil?: string
  remainingMinutes?: number
}> => {
  const response = await apiClient.post('/pin/protected/verify-pin', data)
  return response.data
}

// Change PIN (requires current PIN verification)
export const changePIN = async (data: ChangePINRequest): Promise<{
  success: boolean
  message: string
  data: { message: string }
}> => {
  const response = await apiClient.post('/pin/protected/change-pin', data)
  return response.data
}

// ==================== EMAIL VERIFICATION ====================

// Send email verification OTP
export const sendEmailVerificationOTP = async (): Promise<{
  success: boolean
  message: string
  data: { otpCode?: string; expiresAt: string }
}> => {
  const response = await apiClient.post('/pin/protected/send-email-otp')
  return response.data
}

// Verify email with OTP
export const verifyEmailOTP = async (data: VerifyEmailOTPRequest): Promise<{
  success: boolean
  message: string
  data: { emailVerified: boolean }
}> => {
  const response = await apiClient.post('/pin/protected/verify-email-otp', data)
  return response.data
}

// ==================== PIN RESET (Public) ====================

// Initiate PIN reset process (public - no auth required)
export const initiatePINReset = async (data: InitiatePINResetRequest): Promise<{
  success: boolean
  message: string
  data?: { resetToken?: string; otpCode?: string } // For development only
}> => {
  const response = await apiClient.post('/pin/initiate-pin-reset', data)
  return response.data
}

// Complete PIN reset with OTP verification (public - no auth required)
export const completePINReset = async (data: CompletePINResetRequest): Promise<{
  success: boolean
  message: string
  data: { message: string }
}> => {
  const response = await apiClient.post('/pin/complete-pin-reset', data)
  return response.data
}

// ==================== ADMIN FUNCTIONS ====================

// Reset PIN attempts for a user (admin only)
export const resetUserPINAttempts = async (userId: string): Promise<{
  success: boolean
  message: string
  data: { userId: string; pinAttempts: number }
}> => {
  const response = await apiClient.post(`/pin/protected/reset-pin-attempts/${userId}`)
  return response.data
}